import { Form, ModalProps, message } from "antd";
import { useEffect, useMemo } from "react";
import CustomInput from "../../../commons/components/CustomInput/CustomInput";
import CustomModal from "../../../commons/components/CustomModal/CustomModal";
import CustomPhoneInput from "../../../commons/components/CustomPhoneInput/CustomPhoneInput";
import { CustomSelect } from "../../../commons/components/CustomSelect/CustomSelect";
import GradientText from "../../../commons/components/GradientText/GradientText";
import PrimaryButton from "../../../commons/components/PrimaryButton/PrimaryButton";
import { EMAIL_PATTERN, getCurrentPattern } from "../../../commons/constants";
import { ADD_NEW_MEMBER_MERCHANT, EDIT_MEMBER_MERCHANT } from "../../../commons/constants/api-urls";
import { PREFIXES } from "../../../commons/constants/user";
import defaultAxios from "../../../commons/utils/axios";
import { integerOnlyInput } from "../../../commons/utils/functions/integerOnly";
import removeExtraSpace from "../../../commons/utils/functions/removeExtraSpace";
import styles from "./AddNewMember.module.scss";
import { MerchantMemberBodyRequest, MerchantRole, NewMerchantMember } from "../../../commons/constants/merchant";
import { CustomStoreSelect } from "../../../commons/components/CustomStoreSelect/CustomStoreSelect";
import { STORE_TYPE_TABS } from "../../StoreManagement";
interface AddNewMemberProps extends ModalProps {
  onCancel: () => void;
  member?: MerchantMember;
  isEdit?: boolean;
  refresh: () => void;
  open: boolean;
}
const AddNewMember: React.FC<AddNewMemberProps> = ({
  member,
  isEdit,
  onCancel,
  refresh,
  open,
  ...props
}: AddNewMemberProps) => {
  const [form] = Form.useForm<NewMerchantMember>();
  const role = Form.useWatch(["role"], form);

  const getStoreSelectionType = (role: string) => {
    if (!role) return STORE_TYPE_TABS.ALL;

    switch (role) {
      case MerchantRole.MEMBER:
        return STORE_TYPE_TABS.ALL;
      case MerchantRole.DRIVER:
        return STORE_TYPE_TABS.FOOD_DELIVERY;
      default:
        return STORE_TYPE_TABS.ALL;
    }
  };

  const storeSelectionType = useMemo(() => {
    return getStoreSelectionType(role);
  }, [role]);

  const onFinishCreateMember = async (values: NewMerchantMember) => {
    const bodyAddMemberMerchant: MerchantMemberBodyRequest = {
      email: values.email,
      phone: values.phone,
      merchant_stores_ids: [values.merchant_stores_ids as unknown as string],
      phoneCode: values.region.replace("+", ""),
      role: values.role,
    };

    try {
      form.resetFields();
      await defaultAxios.post<any>(ADD_NEW_MEMBER_MERCHANT, bodyAddMemberMerchant);
      message.success("Add new member successfully");
      refresh();
    } catch (error: any) {
      message.error(error?.response?.data?.error?.message || "Add new member failed");
    }
    onCancel();
  };

  const onFinishEditMember = async (values: MerchantMember) => {
    const bodyEditMemberMerchant = {
      storeIds: [values.merchant_stores_ids],
      merchant_member_id: Number(member?.merchant_member_id),
    };
    try {
      await defaultAxios.put(EDIT_MEMBER_MERCHANT(), bodyEditMemberMerchant);
      message.success("Edit member successfully");
      form.resetFields();
      refresh();
    } catch (error) {
      message.error("Edit member failed");
    }
    onCancel();
  };

  const handleChangeRole = (e: string) => {
    form.setFieldValue("role", e);
    const merchantStoreIds = form.getFieldValue("merchant_stores_ids");
    if (merchantStoreIds) {
      form.setFieldValue("merchant_stores_ids", null);
    }
  };

  function handleCloseModal() {
    form.resetFields();
    onCancel();
  }
  const region = Form.useWatch("region", form);

  const phonePattern = useMemo(() => getCurrentPattern(region), [region]);

  useEffect(() => {
    if (open) {
      form.setFieldValue("region", PREFIXES[0].value);
    }
  }, [form, open]);

  useEffect(() => {
    if (!member || !open) {
      return;
    }
    console.log("member: ", member);
    form.setFieldsValue(member);
  }, [member, form]);

  if (isEdit) {
    return (
      <CustomModal open={open} {...props} title={`Edit member store`}>
        <Form onFinish={onFinishEditMember} form={form}>
          <div className={styles.label}>Member Store</div>
          <CustomStoreSelect
            placeholder="Select store"
            name="merchant_stores_ids"
            storeType={getStoreSelectionType(member?.role)}
            rules={[
              {
                required: true,
                message: "Please select store!",
              },
            ]}
            labelValue={
              member?.merchant_stores_names && member?.merchant_stores_names.length > 0
                ? member?.merchant_stores_names[0]
                : ""
            }
          />
          <div className={styles.groupButton}>
            <div className={styles.btnCancel} onClick={handleCloseModal}>
              <GradientText text={"Cancel"}></GradientText>
            </div>
            <PrimaryButton type="submit" className={styles.btnConfirm}>
              Save
            </PrimaryButton>
          </div>
        </Form>
      </CustomModal>
    );
  } else {
    return (
      <CustomModal open={open} {...props} title={`${isEdit ? "Edit" : "Add new"} member`}>
        <Form onFinish={onFinishCreateMember} form={form}>
          <div className={styles.label}>Email</div>
          <CustomInput
            name="email"
            placeholder="Enter member email"
            rules={[
              {
                required: true,
                message: "Please input email!",
                whitespace: true,
              },
              {
                pattern: EMAIL_PATTERN,
                message: "Invalid email",
              },
            ]}
            onBlur={() => removeExtraSpace("email", form)}
          />
          <div className={styles.label}>Member Phone</div>
          <CustomPhoneInput
            formInstance={form}
            name="phone"
            placeholder="Enter member phone number"
            rules={[
              {
                required: true,
                message: "Please input phone number!",
              },
              {
                pattern: phonePattern,
                message: "Invalid phone number",
              },
            ]}
            onKeyDown={integerOnlyInput}
            onBlur={() => removeExtraSpace("phone", form)}
          />
          <div className={styles.label}>Member Store</div>
          <CustomSelect
            placeholder="Select role"
            options={Object.keys(MerchantRole)
              .filter(item => item !== MerchantRole.ADMIN)
              .map(item => ({ label: item, value: item }))}
            name="role"
            rules={[
              {
                required: true,
                message: "Please select merchant role!",
              },
            ]}
            onChange={handleChangeRole}
          />
          <CustomStoreSelect
            placeholder="Select store"
            name="merchant_stores_ids"
            disabled={!role}
            storeType={storeSelectionType}
            rules={[
              {
                required: true,
                message: "Please select store!",
              },
            ]}
          />
          <div className={styles.groupButton}>
            <div className={styles.btnCancel} onClick={handleCloseModal}>
              <GradientText text={"Cancel"}></GradientText>
            </div>
            <PrimaryButton type="submit" className={styles.btnConfirm}>
              {isEdit ? "Save" : "Add member"}
            </PrimaryButton>
          </div>
        </Form>
      </CustomModal>
    );
  }
};

export default AddNewMember;
